import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import ControlTrendSheet from "../components/ControlTrendSheet";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fmtInt } from "../lib/format";
import TrendButton from "../components/TrendButton";
import StatCard from "../components/ciso/StatCard";
import SectionPanel from "../components/ciso/SectionPanel";
import Tile from "../components/ciso/Tile";
import DonutGauge from "../components/ciso/DonutGauge";
import ControlCardCiso from "../components/ciso/ControlCardCiso";

const ragCiso = (score) =>
  score == null ? "var(--ink)" : score < 90 ? "var(--critical-600)" : score < 95 ? "var(--tan)" : "var(--success-600)";

const pct = (n, d) => (d ? Math.round((n / d) * 1000) / 10 : 0);

export default function Control() {
  const [kind, setKind] = useState("workstation");
  const [summary, setSummary] = useState(null);
  const [ciso, setCiso] = useState(null);
  const [openControl, setOpenControl] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    apiGet("/control/summary", { kind }).then(setSummary);
    apiGet("/control/ciso", { kind }).then(setCiso);
  }, [kind]);

  const drill = (dataset, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    nav(`/drill/${dataset}${qs ? `?${qs}` : ""}`);
  };

  return (
    <>
      <Masthead
        module="control"
        eyebrow="Control Assurance"
        title="Endpoint Control Posture"
        subtitle="Agent coverage, prevention baselines & compliance — click any widget to drill to records"
        meta={ciso && (
          <div className="flex gap-3 text-[13px]">
            <span><strong style={{ color: "var(--success-600)" }}>{ciso.compliant}</strong> Compliant</span>
            <span><strong style={{ color: "var(--tan)" }}>{ciso.at_risk}</strong> At risk</span>
            <span><strong style={{ color: "var(--critical-600)" }}>{ciso.non_compliant}</strong> Non-compliant</span>
          </div>
        )}
      />

      <div className="ciso flex-1" style={{ padding: "22px 26px 48px" }}>
        <div className="flex flex-col gap-4" style={{ maxWidth: 1680 }}>

          {/* ---------- Top KPI row ---------- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="control-kpi-row">
            <StatCard
              testid="kpi-composite"
              label="Composite assurance"
              value={ciso ? ciso.composite_score : "…"}
              valueColor={ragCiso(ciso?.composite_score)}
              accent="var(--rust-600)"
              icon="ph-fill ph-gauge"
              desc="In-scope-weighted mean of agent health across all controls."
              onClick={() => drill("controls", { kind })}
            />
            <StatCard
              testid="kpi-healthy"
              label="Healthy agents"
              value={ciso ? fmtInt(ciso.total_healthy) : "…"}
              valueColor="var(--success-600)"
              accent="var(--success-500)"
              icon="ph-fill ph-shield-check"
              iconColor="var(--success-600)"
              desc="Endpoints reporting a healthy, up-to-date agent."
              onClick={() => drill("control_endpoints", { agent_status: "healthy" })}
            />
            <StatCard
              testid="kpi-unhealthy"
              label="Unhealthy agents"
              value={ciso ? fmtInt(ciso.total_unhealthy) : "…"}
              valueColor="var(--tan)"
              accent="var(--amber-500)"
              icon="ph-fill ph-warning-circle"
              iconColor="var(--tan)"
              desc="Agents installed but stale, degraded or misconfigured."
              onClick={() => drill("control_endpoints", { agent_status: "unhealthy" })}
            />
            <StatCard
              testid="kpi-missing"
              label="Missing agents"
              value={ciso ? fmtInt(ciso.total_missing) : "…"}
              valueColor="var(--critical-600)"
              accent="var(--critical-600)"
              icon="ph-fill ph-plugs"
              iconColor="var(--critical-600)"
              desc="In-scope endpoints with no agent present at all."
              onClick={() => drill("control_endpoints", { agent_status: "missing" })}
            />
          </div>

          {/* ---------- Insight panels ---------- */}
          {ciso && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Agent coverage */}
              <SectionPanel
                testid="panel-coverage"
                icon="ph-fill ph-desktop"
                title="Agent coverage"
                subtitle="Endpoints in scope across all controls"
                live
                action={
                  <TrendButton widgetId={`control-coverage-${kind}`} label="Agent coverage %" module="control" unit="percent" current={ciso.coverage_pct} testid="panel-coverage-view-trend" />
                }
              >
                <div className="grid grid-cols-2 gap-[11px]">
                  <Tile testid="tile-inscope" tone="neutral" label="In scope" value={fmtInt(ciso.total_in_scope)} sub="all endpoints" onClick={() => drill("control_endpoints")} />
                  <Tile testid="tile-healthy" tone="success" label="Healthy" value={fmtInt(ciso.total_healthy)} sub="agent OK" valueColor="var(--success-600)" onClick={() => drill("control_endpoints", { agent_status: "healthy" })} />
                  <Tile testid="tile-unhealthy" tone="warning" label="Unhealthy" value={fmtInt(ciso.total_unhealthy)} sub="degraded" valueColor="var(--tan)" onClick={() => drill("control_endpoints", { agent_status: "unhealthy" })} />
                  <Tile testid="tile-missing" tone="critical" label="Missing" value={fmtInt(ciso.total_missing)} sub="no agent" valueColor="var(--critical-600)" onClick={() => drill("control_endpoints", { agent_status: "missing" })} />
                </div>
                <DonutGauge
                  heading="Coverage distribution"
                  pct={ciso.coverage_pct}
                  centerLabel={`${ciso.coverage_pct}%`}
                  centerSub="Healthy"
                  arcColor="var(--success-500)"
                  legend={[
                    { label: "Healthy", pct: pct(ciso.total_healthy, ciso.total_in_scope), color: "var(--success-500)" },
                    { label: "Unhealthy", pct: pct(ciso.total_unhealthy, ciso.total_in_scope), color: "var(--amber-500)" },
                    { label: "Missing", pct: pct(ciso.total_missing, ciso.total_in_scope), color: "var(--critical-600)" },
                  ]}
                />
              </SectionPanel>

              {/* Control status */}
              <SectionPanel
                testid="panel-status"
                icon="ph-fill ph-list-checks"
                title="Control status"
                subtitle="Compliance against the 90 / 95% thresholds"
                live
              >
                <div className="grid grid-cols-2 gap-[11px]">
                  <Tile testid="tile-total" tone="neutral" label="Total controls" value={ciso.total_controls} sub="in this scope" onClick={() => drill("controls", { kind })} />
                  <Tile testid="tile-compliant" tone="success" label="Compliant" value={ciso.compliant} sub="≥ 95% healthy" valueColor="var(--success-600)" onClick={() => drill("controls", { kind, status: "Compliant" })} />
                  <Tile testid="tile-atrisk" tone="warning" label="At risk" value={ciso.at_risk} sub="90–95%" valueColor="var(--tan)" onClick={() => drill("controls", { kind, status: "At risk" })} />
                  <Tile testid="tile-noncompliant" tone="critical" label="Non-compliant" value={ciso.non_compliant} sub="< 90%" valueColor="var(--critical-600)" onClick={() => drill("controls", { kind, status: "Non-compliant" })} />
                </div>
                <DonutGauge
                  heading="Status distribution"
                  pct={pct(ciso.compliant, ciso.total_controls)}
                  centerLabel={`${pct(ciso.compliant, ciso.total_controls)}%`}
                  centerSub="Compliant"
                  arcColor="var(--success-500)"
                  legend={[
                    { label: "Compliant", pct: pct(ciso.compliant, ciso.total_controls), color: "var(--success-500)" },
                    { label: "At risk", pct: pct(ciso.at_risk, ciso.total_controls), color: "var(--amber-500)" },
                    { label: "Non-compliant", pct: pct(ciso.non_compliant, ciso.total_controls), color: "var(--critical-600)" },
                  ]}
                />
              </SectionPanel>
            </div>
          )}

          {/* ---------- Threshold legend ---------- */}
          <div className="ciso-panel flex flex-wrap items-center gap-4" data-testid="threshold-legend" style={{ padding: "14px 20px" }}>
            <div className="text-[12px] font-bold ciso-cap" style={{ color: "var(--text-muted)" }}>
              Compliance thresholds
            </div>
            {[
              ["Non-compliant", "below 90% healthy", "var(--critical-600)", "var(--critical-50)"],
              ["At risk", "90% – 95%", "var(--tan)", "var(--warning-50)"],
              ["Compliant", "above 95%", "var(--success-600)", "var(--success-50)"],
            ].map(([label, note, fg, bg]) => (
              <div key={label} className="flex items-center gap-2 text-[13px]">
                <span className="text-[11px] font-bold" style={{ padding: "2px 9px", borderRadius: "var(--radius-pill)", background: bg, color: fg }}>{label}</span>
                <span style={{ color: "var(--text-muted)" }}>{note}</span>
              </div>
            ))}
          </div>

          {/* ---------- Tabs + per-control cards ---------- */}
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList data-testid="control-tabs">
              <TabsTrigger value="workstation" data-testid="tab-workstation">Workstations</TabsTrigger>
              <TabsTrigger value="server" data-testid="tab-server">Servers</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="control-cards">
            {summary?.controls?.map((c) => (
              <ControlCardCiso
                key={c.id}
                c={c}
                onOpenSheet={setOpenControl}
                onDrill={() => drill("control_endpoints")}
              />
            ))}
          </div>
        </div>
      </div>

      <ControlTrendSheet
        open={!!openControl}
        onOpenChange={(v) => { if (!v) setOpenControl(null); }}
        control={openControl}
      />
    </>
  );
}
